import type React from 'react';
import { DateInput } from './DateInput.js';

export type RecipientDraft = { name: string; email: string; role: 'SIGNER' | 'VIEWER' | 'APPROVER' };

/** OPS 3.5 #8: the recipients row, aligned — name and email flex, the role
 *  select gets a fixed width on sm+ (a w-full select inside the flex row was
 *  what broke the layout), the remove button never stretches. */
export function RecipientsEditor({ recipients, onChange, inputCls }: {
  recipients: RecipientDraft[];
  onChange: (next: RecipientDraft[]) => void;
  inputCls: string;
}) {
  return (
    <div className="space-y-2">
      {recipients.map((r, i) => (
        <div key={i} className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            value={r.name}
            onChange={(e) => onChange(recipients.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
            placeholder="Name"
            className={`flex-1 min-w-0 ${inputCls}`}
          />
          <input
            type="email"
            value={r.email}
            onChange={(e) => onChange(recipients.map((x, idx) => (idx === i ? { ...x, email: e.target.value } : x)))}
            placeholder="Email"
            className={`flex-1 min-w-0 ${inputCls}`}
          />
          <select
            value={r.role}
            onChange={(e) => onChange(recipients.map((x, idx) => (idx === i ? { ...x, role: e.target.value as RecipientDraft['role'] } : x)))}
            className={`sm:w-32 sm:shrink-0 ${inputCls}`}
          >
            <option value="SIGNER">Signer</option>
            <option value="VIEWER">Viewer</option>
            <option value="APPROVER">Approver</option>
          </select>
          <button
            type="button"
            onClick={() => onChange(recipients.filter((_, idx) => idx !== i))}
            className="shrink-0 self-start sm:self-center text-rose-300 hover:text-rose-200 text-sm px-2 py-1"
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...recipients, { name: '', email: '', role: 'SIGNER' }])}
        className="text-sm text-accent hover:underline"
      >
        + Add recipient
      </button>
    </div>
  );
}

/** OPS 3.5 #9: the ONE proposal details card — subject / body / expiry /
 *  signer / other recipients — rendered by BOTH the composer (create) and the
 *  edit modal, so the two can never drift. Validation stays in the host.
 *  `signerEmail` shown read-only only where the client signer is auto-added
 *  (the composer); the edit modal edits the full recipients array instead. */
export function ProposalDetailsForm({ subject, onSubjectChange, subjectPlaceholder, body, onBodyChange, bodyRequired, expiresAt, onExpiresAtChange, signerEmail, recipients, onRecipientsChange, recipientsLabel, inputCls }: {
  subject: string;
  onSubjectChange: (v: string) => void;
  subjectPlaceholder?: string;
  body: string;
  onBodyChange: (v: string) => void;
  bodyRequired?: boolean;
  expiresAt: string;
  onExpiresAtChange: (v: string) => void;
  signerEmail?: string;
  recipients: RecipientDraft[];
  onRecipientsChange: (next: RecipientDraft[]) => void;
  recipientsLabel?: string;
  inputCls: string;
}) {
  return (
    <>
      <div>
        <label className="block text-xs text-muted mb-1">Subject <span className="text-rose-400">*</span></label>
        <input
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder={subjectPlaceholder ?? 'Proposal subject'}
          className={inputCls}
          required
        />
      </div>
      <div>
        <label className="block text-xs text-muted mb-1">Body {bodyRequired && <span className="text-rose-400">*</span>}</label>
        <textarea
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          rows={5}
          placeholder="Intro, scope notes and anything the client should read before the item table."
          className={inputCls}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-muted mb-1">Expires at <span className="text-rose-400">*</span></label>
          <DateInput value={expiresAt} onChange={(e) => onExpiresAtChange(e.target.value)} className={inputCls} required />
        </div>
        {signerEmail !== undefined && (
          <div>
            <label className="block text-xs text-muted mb-1">Client signer</label>
            <input readOnly value={signerEmail} placeholder="Auto-added from the client record" className={`${inputCls} bg-bg-card/60`} />
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs text-muted mb-1">{recipientsLabel ?? 'Other signers, viewers & approvers'}</label>
        <RecipientsEditor recipients={recipients} onChange={onRecipientsChange} inputCls={inputCls} />
      </div>
    </>
  );
}
